import React, { useState } from 'react';
import { Sun, Check, X, ExternalLink, History } from 'lucide-react';
import type { DailyDigest, DigestTopItem } from '@/services/api/agentApi';
import { reconciliationApi, promotionsApi, filingsApi, digestApi } from '@/services/api/agentApi';
import { Button } from '@/components/ui/button';

const URGENCY_COLOR: Record<DigestTopItem['urgency'], string> = {
  high: 'bg-red-500/10 text-red-600 border-red-500/30',
  medium: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  low: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
};

interface Props {
  digest: DailyDigest | DigestPayload;
  onOpenPastMornings?: () => void;
  onItemActioned?: () => void;
}

/**
 * The chat-thread payload shape — narrower than the persisted DailyDigest.
 * Backend emits this via `user_agent:digest` Socket.IO event.
 */
export interface DigestPayload {
  digest_id: string;
  narrative_text: string;
  top_items: DigestTopItem[];
  agent_voice?: { agent_name?: string; personality_snippet?: string };
  generated_at: string;
}

export const DigestMessage: React.FC<Props> = ({ digest, onOpenPastMornings, onItemActioned }) => {
  const [items, setItems] = useState<DigestTopItem[]>(digest.top_items);
  const [busy, setBusy] = useState<string | null>(null);

  const handleAction = async (item: DigestTopItem, action: 'approve' | 'reject') => {
    setBusy(item.id);
    try {
      if (item.kind === 'proposal') {
        if (action === 'approve') await reconciliationApi.approve(item.proposal_id || item.id);
        else await reconciliationApi.reject(item.proposal_id || item.id);
      } else if (item.kind === 'promotion') {
        if (action === 'approve') await promotionsApi.approve(item.id);
        else await promotionsApi.reject(item.id);
      } else if (item.kind === 'filing') {
        if (action === 'approve') await filingsApi.approve(item.id);
        else await filingsApi.reject(item.id);
      }
      // Optimistically remove from the visible list
      setItems(prev => prev.filter(i => i.id !== item.id));
      onItemActioned?.();
    } catch {
      // Leave item in place on error
    } finally {
      setBusy(null);
    }
  };

  const markRead = async () => {
    if ('read_at' in digest && digest.read_at) return;
    try {
      await digestApi.markRead(digest.digest_id);
    } catch {
      // best-effort
    }
  };

  return (
    <div
      className="border rounded-lg p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200/50 dark:border-amber-900/30"
      onMouseEnter={() => void markRead()}
    >
      <div className="flex items-center gap-2 mb-3">
        <Sun className="h-4 w-4 text-amber-600" />
        <span className="text-xs font-medium text-amber-900 dark:text-amber-200">
          Morning brief
          {digest.agent_voice?.agent_name ? ` — ${digest.agent_voice.agent_name}` : ''}
        </span>
        <span className="text-xs text-muted-foreground ml-auto">
          {new Date(digest.generated_at).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
        </span>
      </div>

      <div className="text-sm whitespace-pre-wrap mb-4 leading-relaxed">
        {digest.narrative_text}
      </div>

      {items.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Action items ({items.length})
          </div>
          {items.map(item => (
            <div
              key={`${item.kind}:${item.id}`}
              className={`border rounded-md p-3 ${URGENCY_COLOR[item.urgency]}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium mb-1">{item.title}</p>
                  <p className="text-xs opacity-80">{item.summary}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  {item.kind !== 'task' ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === item.id}
                        onClick={() => handleAction(item, 'approve')}
                        title="Approve"
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === item.id}
                        onClick={() => handleAction(item, 'reject')}
                        title="Dismiss"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" title="Open">
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {onOpenPastMornings && (
        <button
          onClick={onOpenPastMornings}
          className="mt-4 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <History className="h-3 w-3" />
          View past mornings
        </button>
      )}
    </div>
  );
};
